import { resetChangeTripsSelection } from '@/features/manage/manageActions';
import {
  getAllUpgradeExchangeOffers,
  setSelectedInboundJourney,
  setSelectedOutboundJourney,
} from '@/features/purchase/purchaseActions';
import { useChangeBookingSelection } from '@/hooks/useChangeBookingSelection';
import { TransAlert } from '@/i18n/trans/alert';
import { useDispatch } from '@/store/utils';
import type { ApiErrorResponse } from '@/types/api';
import type { ErrorDTO } from '@/types/dto/agent';
import {
  getAlertReasonFromApiError,
  type AlertReason,
} from '@/utils/changeTrips';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

interface UseChangeTravelClassProps {
  isOutbound?: boolean;
}

const showStartExchangeError = (err?: unknown) => {
  const errors = (err as ApiErrorResponse | undefined)?.response?.data?.errors;
  if (errors?.length) {
    errors.forEach((error: ErrorDTO) => toast.error(error.message));
    return;
  }
  toast.error(<TransAlert i18nKey="startExchangeFailed" />);
};

export const useChangeTravelClass = ({
  isOutbound = true,
}: UseChangeTravelClassProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { changeFlow, selectedOutboundTrip, selectedInboundTrip } =
    useChangeBookingSelection();
  const isChangeTravelClassFlow = changeFlow === 'changeTravelClass';

  const getChangeTravelClassOffers = useCallback(async (): Promise<
    true | AlertReason | null
  > => {
    if (!isChangeTravelClassFlow) {
      return null;
    }

    if (!selectedOutboundTrip?.id && !selectedInboundTrip?.id) {
      dispatch(resetChangeTripsSelection());
      navigate('/manage-booking/details');
      return null;
    }
    try {
      const result = await dispatch(getAllUpgradeExchangeOffers()).unwrap();
      const firstOutboundJourney = result.outbound?.journeys?.[0];
      const firstInboundJourney = result.inbound?.journeys?.[0];
      if (!firstOutboundJourney && !firstInboundJourney) {
        showStartExchangeError();
        return null;
      }
      if (firstOutboundJourney) {
        dispatch(setSelectedOutboundJourney(firstOutboundJourney));
      }
      if (firstInboundJourney) {
        dispatch(setSelectedInboundJourney(firstInboundJourney));
      }
      return true;
    } catch (err) {
      const errors = (err as ApiErrorResponse | undefined)?.response?.data
        ?.errors;
      if (errors?.length) {
        const alertReason = getAlertReasonFromApiError(errors[0]);
        if (alertReason) {
          return alertReason;
        }
      }
      showStartExchangeError(err);
      return null;
    }
  }, [
    dispatch,
    isChangeTravelClassFlow,
    navigate,
    selectedInboundTrip?.id,
    selectedOutboundTrip?.id,
  ]);

  const handleGoBack = useCallback(() => {
    if (!isChangeTravelClassFlow) {
      return;
    }
    if (!isOutbound && selectedOutboundTrip?.id) {
      navigate('/purchase/outbound/offer');
      return;
    }
    navigate('/manage-booking/details', {
      state: { reopenChangeTripsModal: true },
    });
  }, [isChangeTravelClassFlow, isOutbound, navigate, selectedOutboundTrip?.id]);

  return {
    isChangeTravelClassFlow,
    getChangeTravelClassOffers,
    handleGoBack,
    showStartExchangeError,
  };
};
